import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useStaticQuery, graphql } from "gatsby";
import { useIntl, navigate } from "gatsby-plugin-intl";
import { GatsbyImage, StaticImage, getImage } from "gatsby-plugin-image";
import get from "lodash/get";
import Slider from "react-slick";

import KCArrowControls from "Components/common/kcArrowControls";

const StyledWrapper = styled.div`
  background-color: ${(props) => props.theme.color[props.backgroundColor]};
  padding: 2rem 0rem;
  display: flex;
  flex-direction: column;
`;

const StyledSocialPost = styled.div`
  margin-left: 0.5rem;
  width: calc(100% - 0.5rem) !important;
  outline: none;
  &:first-of-type {
    margin-left: 1rem;
  }
`;

const StyledHeader = styled.div`
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-box: content-box;
  width: 100%;
`;

const StyledTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTitle = styled.div`
  font-weight: 600;
  font-size: 2rem;
  text-align: left;
  line-height: 1.8rem;
`;

const StyledSubtitle = styled.div`
  font-size: 0.7rem;
  color: ${(props) => props.theme.color.grey};
  display: flex;
  align-items: center;

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

const StyledInstagramIcon = styled.div`
  width: 12px;
  height: 12px;
  opacity: 0.4;
  margin-right: 4px;
`;

const StyledCarousel = styled(Slider)`
  width: 100%;
  margin-top: 1rem;
  & .slick-list {
    padding: 0 20% 0 0;
  }
`;

const StyledImageWrapper = styled.div`
  height: 18rem;
  overflow: hidden;
`;

const StyledCaption = styled.div`
  margin-top: 0.6rem;
  font-size: 0.8rem;
  width: 90%;
  color: ${(props) => props.theme.color.darkGrey};
`;

const SocialPost = ({ photo, caption }) => {
  return (
    <StyledSocialPost>
      <StyledImageWrapper>
        {photo && (
          <GatsbyImage
            style={{ width: "100%", height: "100%" }}
            objectFit="cover"
            image={getImage(photo.imageFile)}
            alt="photo"
          />
        )}
      </StyledImageWrapper>
      <StyledCaption>{caption}</StyledCaption>
    </StyledSocialPost>
  );
};

const ScrollSettings = {
  slidesToShow: 3.5,
  speed: 500,
  infinite: false,
  arrows: false,
  touchMove: true,
  accessibility: true,
  responsive: [
    {
      breakpoint: 800,
      settings: {
        slidesToShow: 2.5,
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1.5,
      },
    },
  ],
};

export default function SocialModal({
  title,
  designID,
  textColor,
  backgroundColor,
}) {
  const intl = useIntl();
  const { strapi } = useStaticQuery(graphql`
    {
      strapi {
        customerReviews(sort: "priority") {
          spectacleDesign {
            kcID
          }
          priority
          photo {
            imageFile {
              childImageSharp {
                gatsbyImageData
              }
            }
            url
          }
          priority
          caption
        }
      }
    }
  `);

  let customerReviews = strapi.customerReviews;
  if (designID) {
    customerReviews = customerReviews.filter(
      (cr) => cr.spectacleDesign.kcID === designID
    );
  }
  const handleGoToInstagram = () => {
    window.open(
      "https://www.instagram.com/king.children/",
      "_blank" // <- This is what makes it open in a new window.
    );
  };

  const [slideIndex, setSlideIndex] = useState(0);
  const scrollWrapperRef = useRef(null);
  const handleNext = () => {
    if (typeof window !== "undefined") {
      let pageSize = window.innerWidth > 600 ? 3 : 1;
      scrollWrapperRef.current.slickGoTo(slideIndex + pageSize);
    }
  };

  const handlePrevious = () => {
    if (typeof window !== "undefined") {
      let pageSize = window.innerWidth > 600 ? 3 : 1;
      scrollWrapperRef.current.slickGoTo(slideIndex - pageSize);
    }
  };

  return customerReviews.length > 0 ? (
    <StyledWrapper backgroundColor={backgroundColor}>
      <StyledHeader>
        <StyledTitleWrapper>
          <StyledTitle>{get(title, intl.locale, "")}</StyledTitle>
          <StyledSubtitle onClick={() => handleGoToInstagram()}>
            <StyledInstagramIcon>
              <StaticImage
                alt="Instagram"
                src="../../../images/Icon/instagram-white.png"
                imgStyle={{ objectFit: "contain" }}
                style={{ filter: "invert(1)", width: "100%", height: "100%" }}
              />
            </StyledInstagramIcon>
            <>KING.CHILDREN</>
          </StyledSubtitle>
        </StyledTitleWrapper>
        <KCArrowControls
          slideIndex={slideIndex}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          totalSlides={customerReviews.length - 3}
        />
      </StyledHeader>
      <StyledCarousel
        ref={scrollWrapperRef}
        {...ScrollSettings}
        beforeChange={(oldI, newI) => {
          setSlideIndex(newI);
        }}
      >
        {customerReviews.map((customerReview, i) => (
          <SocialPost
            key={`social${i}`}
            photo={customerReview.photo}
            caption={customerReview.caption}
          />
        ))}
      </StyledCarousel>
    </StyledWrapper>
  ) : (
    <></>
  );
}

export const query = graphql`
  fragment socialModal on STRAPI_ComponentBaseSocialModal {
    __typename
    id
    title {
      en
      es
      it
    }
    backgroundColor {
      color
    }
    textColor {
      color
    }
    spectacleStyle {
      kcID
    }
  }
`;
